import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  Tooltip,
  IconButton,
  Divider
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  Info as InfoIcon,
  CallMade,
  CallReceived,
  Edit,
  Sync as EchoIcon
} from '@mui/icons-material';
import { useAuth } from "../../context/AuthContext";
import ServiceTierSelect from "./ServiceTierSelect";
import axios from 'axios';

// Utility function for currency formatting
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

function UsageMetricCard({ title, current, limit, icon: Icon, subtitle }) {
  const percentage = limit ? Math.min((current / limit) * 100, 100) : 0;

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          {Icon && <Icon color="primary" />}
          <Typography variant="h6">{title}</Typography>
          <Tooltip title="Usage for current billing period">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Typography variant="h4" sx={{ mb: 1 }}>
          {current?.toLocaleString() || '0'}
          {limit ? (
            <Typography component="span" variant="body2" color="text.secondary">
              /{limit?.toLocaleString()}
            </Typography>
          ) : null}
        </Typography>

        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        )}

        {limit && (
          <>
            <Box sx={{ width: '100%', mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={percentage}
                color={percentage > 90 ? "error" : percentage > 75 ? "warning" : "primary"}
              />
            </Box>

            <Typography variant="body2" color="text.secondary">
              {percentage.toFixed(1)}% of monthly limit
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function CostBreakdownCard({ title, data, pricing, isLoading }) {
  if (isLoading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="h6" gutterBottom>{title}</Typography>
      <Stack spacing={1}>
        {Object.entries(data).map(([type, count]) => (
          <Box key={type}>
            <Typography variant="body1">
              {type.charAt(0).toUpperCase() + type.slice(1)}:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {count.toLocaleString()} calls × {formatCurrency(pricing[type] / 100)}
              {' = '}{formatCurrency((count * pricing[type]) / 100)}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Card>
  );
}

// Define the known base prices for each tier (in cents)
const TIER_BASE_PRICES = {
  free: 0,
  professional: 7900, // €79.00
  enterprise: 49900  // €499.00
};

export default function UsageBillingPanel({ onNotification }) {
  const { user, fetchUserProfile } = useAuth();
  const [usageData, setUsageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const initialLoadDone = useRef(false);

  // Effect for initial profile load - runs only once on mount
  useEffect(() => {
    const initializeProfile = async () => {
      if (initialLoadDone.current) return;
      
      try {
        setIsLoading(true);
        await fetchUserProfile();
        initialLoadDone.current = true;
      } catch (err) {
        console.error("Error loading initial profile:", err);
      }
    };

    initializeProfile();
  }, []); // Empty dependency array - only runs once

  // Separate effect for loading usage data when service tier changes
  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        setIsLoading(true);
        
        const response = await axios.get('/api/usage/current');

        if (response.data.status === 'success') {
          // Ensure the tier data matches the user's current service tier
          const userData = response.data.data;

          // If the tier name from API doesn't match user's service tier, adjust it
          if (userData.tier.name !== user.serviceTier) {
            console.warn(`API tier (${userData.tier.name}) doesn't match user's tier (${user.serviceTier}). Using user's tier.`);
            userData.tier.name = user.serviceTier;
            // Update basePrice to match the user's service tier
            userData.tier.basePrice = TIER_BASE_PRICES[user.serviceTier] || 0;
            userData.costs.current.base = TIER_BASE_PRICES[user.serviceTier] || 0;
            userData.costs.current.total = userData.costs.current.base +
              Object.values(userData.costs.current.overages || {}).reduce((sum, cost) => sum + cost, 0);
            userData.costs.projected.total = userData.costs.current.base +
              Object.values(userData.costs.projected.overages || {}).reduce((sum, cost) => sum + cost, 0);
          }

          setUsageData(userData);
          setError(null);
        } else {
          throw new Error(response.data.message || 'Failed to fetch usage data');
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to load usage data';
        setError(errorMessage);
        onNotification('Error', errorMessage, 'error');
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch usage data once initialized
    if (initialLoadDone.current) {
      fetchUsageData();
    }
  }, [onNotification, user.serviceTier]);

  const handleServiceTierChange = async (newTier) => {
    try {
      // Update the profile directly (don't call fetchUserProfile here)
      await axios.put("/api/auth/profile", {
        ...user,
        serviceTier: newTier
      });

      // Manually update the local user object to avoid needing another fetch
      user.serviceTier = newTier;
      
      // Refresh usage data after tier change
      const response = await axios.get('/api/usage/current');
      if (response.data.status === 'success') {
        setUsageData(response.data.data);
      }
      onNotification('Success', 'Service tier updated successfully', 'success');
    } catch (error) {
      console.error("Failed to update service tier:", error);
      onNotification('Error', 'Failed to update service tier', 'error');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  // Extract data from the API response
  const {
    usage: {
      current: { billable = {}, nonBillable = {} } = {},
      total: { billable: totalBillable = 0, nonBillable: totalNonBillable = 0, all: totalOperations = 0 } = {},
      percentage: usagePercentage = 0
    } = {},
    tier: {
      monthlyLimit = 1000,
      basePrice = 0,
      pricing = {}
    } = {},
    costs: {
      current: { overages: overageCosts = {}, total: totalCost = 0 } = {},
      projected: { total: projectedTotal = 0 } = {}
    } = {}
  } = usageData || {};

  return (
    <Box component="div" noValidate autoComplete="off">
      <Typography variant="h6" gutterBottom>
        Usage & Billing Overview
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Monitor your API usage, analyze costs, and manage your service tier settings.
      </Typography>

      <Box sx={{ mb: 4 }}>
        <UsageMetricCard
          title="Total API Calls"
          current={totalOperations}
          subtitle={`${totalBillable.toLocaleString()} billable + ${totalNonBillable.toLocaleString()} non-billable calls`}
          icon={BarChartIcon}
        />
      </Box>

      {/* Service Tier Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Service Tier
        </Typography>
        <ServiceTierSelect
          currentTier={user?.serviceTier || "free"}
          onTierChange={handleServiceTierChange}
        />
      </Box>

      {/* Usage Overview Section */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h6">
            Usage Overview
          </Typography>
          <Tooltip title="Current billing period usage metrics">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack spacing={3}>
          <Divider>
            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
              Billable Operations
            </Typography>
          </Divider>

          {/* Billable Operations */}
          <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '280px' }}>
              <UsageMetricCard
                title="Retrievals"
                current={billable.retrieval}
                limit={monthlyLimit / 4}
                icon={CallReceived}
              />
            </Box>

            <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '280px' }}>
              <UsageMetricCard
                title="Submissions"
                current={billable.submission}
                limit={monthlyLimit / 4}
                icon={CallMade}
              />
            </Box>
          </Stack>

          <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '280px' }}>
              <UsageMetricCard
                title="Amendments"
                current={billable.amendment}
                limit={monthlyLimit / 8}
                icon={Edit}
              />
            </Box>

            <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '280px' }}>
              <UsageMetricCard
                title="Retractions"
                current={billable.retraction}
                limit={monthlyLimit / 8}
                icon={Edit}
              />
            </Box>
          </Stack>

          <Divider>
            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
              Non-Billable Operations
            </Typography>
          </Divider>

          <Stack direction="row" spacing={3}>
            <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '280px' }}>
              <UsageMetricCard
                title="Echo Calls"
                current={nonBillable.echo}
                subtitle="Echo API calls"
                icon={EchoIcon}
              />
            </Box>
            <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: '280px' }}>
              <UsageMetricCard
                title="HS Codes"
                subtitle="HS Codes API calls"
                current={nonBillable.hscodes}
                icon={Edit}
              />
            </Box>
          </Stack>
        </Stack>
      </Box>

      {/* Cost Analysis Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Cost Analysis
        </Typography>
        <Stack spacing={3}>
          {/* Current Period Summary */}
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="subtitle1" gutterBottom>
                  Current Billing Period
                </Typography>
                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 auto', minWidth: '200px' }}>
                    <Typography variant="body2" color="text.secondary">
                      Base Plan Cost
                    </Typography>
                    <Typography variant="h6">
                      €{(basePrice / 100).toFixed(2)}/month
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 auto', minWidth: '200px' }}>
                    <Typography variant="body2" color="text.secondary">
                      Current Overage
                    </Typography>
                    <Typography variant="h6" color={Object.values(overageCosts).some(cost => cost > 0) ? 'error.main' : 'text.primary'}>
                      €{(Object.values(overageCosts).reduce((sum, cost) => sum + cost, 0) / 100).toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 auto', minWidth: '200px' }}>
                    <Typography variant="body2" color="text.secondary">
                      Projected Total
                    </Typography>
                    <Typography variant="h6" color="primary">
                      €{(projectedTotal / 100).toFixed(2)}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {/* Cost Breakdown */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Cost Breakdown by Operation
              </Typography>
              <Stack spacing={2}>
                {/* Retrievals */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Retrievals</Typography>
                    <Typography variant="body1">
                      €{((overageCosts.retrieval || 0) / 100).toFixed(2)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {billable.retrieval || 0} calls × €{(pricing.retrieval / 100).toFixed(2)}
                  </Typography>
                </Box>

                {/* Submissions */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Submissions</Typography>
                    <Typography variant="body1">
                      €{((overageCosts.submission || 0) / 100).toFixed(2)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {billable.submission || 0} calls × €{(pricing.submission / 100).toFixed(2)}
                  </Typography>
                </Box>

                {/* Amendments */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Amendments</Typography>
                    <Typography variant="body1">
                      €{((overageCosts.amendment || 0) / 100).toFixed(2)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {billable.amendment || 0} calls × €{(pricing.amendment / 100).toFixed(2)}
                  </Typography>
                </Box>

                {/* Retractions */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">Retractions</Typography>
                    <Typography variant="body1">
                      €{((overageCosts.retraction || 0) / 100).toFixed(2)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {billable.retraction || 0} calls × €{(pricing.retraction / 100).toFixed(2)}
                  </Typography>
                </Box>

                <Divider />

                {/* Total */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2">Total Usage Cost</Typography>
                  <Typography variant="subtitle1" color="primary">
                    €{(totalCost / 100).toFixed(2)}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {/* Usage Recommendations */}
          {usagePercentage > 75 && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Recommendations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Based on your current usage ({usagePercentage.toFixed(1)}% of limit), upgrading to the next tier could save you money.
                  Consider upgrading your plan to avoid overage charges.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Box>
    </Box>
  );
} 